import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { recipes, activities, stories } from '../data/content';
import { useFamily } from '../contexts/FamilyContext';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter, faSort, faBook, faUtensils, faPalette, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import RecipeCard from '../components/dashboard/cards/RecipeCard';
import ActivityCard from '../components/dashboard/cards/ActivityCard';
import StoryCard from '../components/dashboard/cards/StoryCard';
import { PageHeader, Button } from '../design-system';

export default function Explore() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'recipes');
  const navigate = useNavigate();
  const { family } = useFamily();
  const { user } = useAuth();
  
  // Content from Firestore
  const [firestoreRecipes, setFirestoreRecipes] = useState([]);
  const [firestoreActivities, setFirestoreActivities] = useState([]);
  const [firestoreStories, setFirestoreStories] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  
  // Filter and sort state
  const [recipeFilters, setRecipeFilters] = useState({
    difficulty: null,
    sortBy: 'popularity' // 'popularity', 'title', 'time', 'difficulty'
  });
  const [activityFilters, setActivityFilters] = useState({
    category: null,
    age: '',
    sortBy: 'popularity' // 'popularity', 'title', 'time', 'category'
  });
  const [storyFilters, setStoryFilters] = useState({
    category: null,
    age: '',
    sortBy: 'popularity' // 'popularity', 'title', 'category'
  });

  // Clap counts for all content
  const [clapCounts, setClapCounts] = useState({});

  // Load content from Firestore
  useEffect(() => {
    async function loadContent() {
      setLoadingContent(true);
      try {
        // Load recipes (approved or no status field for backward compatibility)
        const recipesQuery = query(
          collection(db, 'content'),
          where('type', '==', 'recipe')
        );
        const recipesSnapshot = await getDocs(recipesQuery);
        const recipesData = recipesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(item => {
            // Include approved content or content without status (for backward compatibility)
            // Exclude explicitly pending content
            return !item.status || item.status === 'approved';
          })
        setFirestoreRecipes(recipesData);

        // Load activities (approved or no status field for backward compatibility)
        const activitiesQuery = query(
          collection(db, 'content'),
          where('type', '==', 'activity')
        );
        const activitiesSnapshot = await getDocs(activitiesQuery);
        const activitiesData = activitiesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(item => {
            // Include approved content or content without status (for backward compatibility)
            // Exclude explicitly pending content
            return !item.status || item.status === 'approved';
          })
        setFirestoreActivities(activitiesData);

        // Load stories (approved or no status field for backward compatibility)
        const storiesQuery = query(
          collection(db, 'content'),
          where('type', '==', 'story')
        );
        const storiesSnapshot = await getDocs(storiesQuery);
        const storiesData = storiesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(item => {
            // Include approved content or content without status (for backward compatibility)
            // Exclude explicitly pending content
            return !item.status || item.status === 'approved';
          })
        setFirestoreStories(storiesData);

        // Load clap counts for all content
        const allContentIds = [
          ...recipesData.map(r => r.id),
          ...activitiesData.map(a => a.id),
          ...storiesData.map(s => s.id),
          ...recipes.map(r => r.id).filter(Boolean),
          ...activities.map(a => a.id).filter(Boolean),
          ...stories.map(s => s.id).filter(Boolean)
        ];
        
        const uniqueContentIds = [...new Set(allContentIds)];
        const clapData = {};
        
        // Batch fetch clap counts
        await Promise.all(
          uniqueContentIds.map(async (contentId) => {
            try {
              const clapDoc = await getDoc(doc(db, 'claps', contentId));
              if (clapDoc.exists()) {
                clapData[contentId] = clapDoc.data().count || 0;
              } else {
                clapData[contentId] = 0;
              }
            } catch (error) {
              console.error(`Error loading claps for ${contentId}:`, error);
              clapData[contentId] = 0;
            }
          })
        );
        
        setClapCounts(clapData);
      } catch (error) {
        console.error('Error loading content from Firestore:', error);
        // Fall back to hardcoded content on error
      }
      setLoadingContent(false);
    }
    
    loadContent();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['recipes', 'activities', 'stories'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Reset filters when switching tabs
  useEffect(() => {
    setRecipeFilters({ difficulty: null, sortBy: 'popularity' });
    setActivityFilters({ category: null, age: '', sortBy: 'popularity' });
    setStoryFilters({ category: null, age: '', sortBy: 'popularity' });
  }, [activeTab]);

  const kidsAges = family?.kids?.map((kid) => parseInt(kid.age)) || [];

  // Use Firestore content if available, otherwise fall back to hardcoded
  const currentRecipes = firestoreRecipes.length > 0 ? firestoreRecipes : recipes;
  const currentActivities = firestoreActivities.length > 0 ? firestoreActivities : activities;
  const currentStories = firestoreStories.length > 0 ? firestoreStories : stories;

  // Get unique filter options
  const recipeDifficulties = useMemo(() => {
    return [...new Set(currentRecipes.map(r => r.difficulty).filter(Boolean))].sort();
  }, [currentRecipes]);

  const activityCategories = useMemo(() => {
    return [...new Set(currentActivities.map(a => a.category).filter(Boolean))].sort();
  }, [currentActivities]);

  const storyCategories = useMemo(() => {
    return [...new Set(currentStories.map(s => s.category).filter(Boolean))].sort();
  }, [currentStories]);

  // Filter and sort functions
  const getFilteredAndSortedRecipes = useMemo(() => {
    let filtered = [...currentRecipes];
    
    if (recipeFilters.difficulty) {
      filtered = filtered.filter(r => r.difficulty === recipeFilters.difficulty);
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (recipeFilters.sortBy) {
        case 'popularity':
          const clapsA = clapCounts[a.id] || 0;
          const clapsB = clapCounts[b.id] || 0;
          // Sort by claps descending, then by title ascending
          if (clapsB !== clapsA) {
            return clapsB - clapsA;
          }
          return a.title.localeCompare(b.title);
        case 'time':
          // Extract numeric time (handles formats like "3 hours", "1.5 hours", "45 minutes", "2-3 hours")
          const getTimeMinutes = (timeStr) => {
            if (!timeStr) return Infinity;
            // Handle ranges like "2-3 hours" by taking the first number
            const rangeMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
            if (rangeMatch) {
              const firstNum = parseFloat(rangeMatch[1]);
              const hours = timeStr.match(/hour/i) ? firstNum : 0;
              const minutes = timeStr.match(/min/i) ? firstNum : 0;
              return hours * 60 + minutes;
            }
            // Handle single values like "1.5 hours" or "45 minutes"
            const hoursMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*hour/i);
            const minutesMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*min/i);
            const hours = hoursMatch ? parseFloat(hoursMatch[1]) : 0;
            const minutes = minutesMatch ? parseFloat(minutesMatch[1]) : 0;
            return hours * 60 + minutes;
          };
          return getTimeMinutes(a.time) - getTimeMinutes(b.time);
        case 'difficulty':
          const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
          return (difficultyOrder[a.difficulty] || 99) - (difficultyOrder[b.difficulty] || 99);
        case 'title':
        default:
          return a.title.localeCompare(b.title);
      }
    });
    
    return filtered;
  }, [recipeFilters, currentRecipes, clapCounts]);

  const getFilteredAndSortedActivities = useMemo(() => {
    let filtered = [...currentActivities];
    
    // Note: Removed automatic age-based filtering - users can use the age filter dropdown if they want
    
    if (activityFilters.category) {
      filtered = filtered.filter(a => a.category === activityFilters.category);
    }
    
    // Filter by age if explicitly provided by user
    if (activityFilters.age) {
      const ageNum = parseInt(activityFilters.age);
      if (!isNaN(ageNum)) {
        filtered = filtered.filter((activity) => {
          if (!activity.ageRange) return false;
          const [minAge, maxAge] = activity.ageRange.split('-').map(Number);
          return ageNum >= minAge && ageNum <= maxAge;
        });
      }
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (activityFilters.sortBy) {
        case 'popularity':
          const clapsA = clapCounts[a.id] || 0;
          const clapsB = clapCounts[b.id] || 0;
          // Sort by claps descending, then by title ascending
          if (clapsB !== clapsA) {
            return clapsB - clapsA;
          }
          return a.title.localeCompare(b.title);
        case 'time':
          // Extract numeric time (handles formats like "30-45 minutes", "1 hour", "20-30 minutes")
          const getTimeMinutes = (timeStr) => {
            if (!timeStr) return Infinity;
            // Handle ranges like "20-30 minutes" by taking the first number
            const rangeMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
            if (rangeMatch) {
              const firstNum = parseFloat(rangeMatch[1]);
              const hours = timeStr.match(/hour/i) ? firstNum : 0;
              const minutes = timeStr.match(/min/i) ? firstNum : 0;
              return hours * 60 + minutes;
            }
            // Handle single values
            const hoursMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*hour/i);
            const minutesMatch = timeStr.match(/(\d+(?:\.\d+)?)\s*min/i);
            const hours = hoursMatch ? parseFloat(hoursMatch[1]) : 0;
            const minutes = minutesMatch ? parseFloat(minutesMatch[1]) : 0;
            return hours * 60 + minutes;
          };
          return getTimeMinutes(a.time) - getTimeMinutes(b.time);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'title':
        default:
          return a.title.localeCompare(b.title);
      }
    });
    
    return filtered;
  }, [activityFilters, kidsAges, clapCounts]);

  const getFilteredAndSortedStories = useMemo(() => {
    let filtered = [...currentStories];
    
    if (storyFilters.category) {
      filtered = filtered.filter(s => s.category === storyFilters.category);
    }
    
    // Filter by age if provided
    if (storyFilters.age) {
      const ageNum = parseInt(storyFilters.age);
      if (!isNaN(ageNum)) {
        filtered = filtered.filter((story) => {
          if (!story.ageRange) return false;
          const [minAge, maxAge] = story.ageRange.split('-').map(Number);
          return ageNum >= minAge && ageNum <= maxAge;
        });
      }
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (storyFilters.sortBy) {
        case 'popularity':
          const clapsA = clapCounts[a.id] || 0;
          const clapsB = clapCounts[b.id] || 0;
          // Sort by claps descending, then by title ascending
          if (clapsB !== clapsA) {
            return clapsB - clapsA;
          }
          return a.title.localeCompare(b.title);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        case 'title':
        default:
          return a.title.localeCompare(b.title);
      }
    });
    
    return filtered;
  }, [storyFilters, currentStories, clapCounts]);

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Explore Content"
        subtitle="Recipes, activities, and stories to enrich your Jewish family life"
        actions={
          user && (
            <Button
              onClick={() => navigate('/submit-content')}
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              <span className="hidden sm:inline">Share Your Own</span>
              <span className="sm:hidden">Share</span>
            </Button>
          )
        }
      >
        {/* Tabs - Full width border on desktop */}
        <div className="overflow-x-auto no-scrollbar border-t border-b border-border -mx-4 px-4 md:-mx-10 md:px-10">
          <div className="flex gap-2 sm:gap-3 min-w-max md:min-w-0">
            <button
              onClick={() => setActiveTab('recipes')}
              className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-semibold border-b-2 transition-colors touch-target whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'recipes'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
              }`}
            >
              <FontAwesomeIcon icon={faUtensils} className="w-4 h-4" />
              Recipes
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-semibold border-b-2 transition-colors touch-target whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'activities'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
              }`}
            >
              <FontAwesomeIcon icon={faPalette} className="w-4 h-4" />
              Activities
            </button>
            <button
              onClick={() => setActiveTab('stories')}
              className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-semibold border-b-2 transition-colors touch-target whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'stories'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
              }`}
            >
              <FontAwesomeIcon icon={faBookOpen} className="w-4 h-4" />
              Stories
            </button>
          </div>
        </div>

        {/* Filters and Sort */}
        {/* Filters - Mobile optimized */}
        <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-3 sm:gap-4 py-2 sm:py-3">
          {/* Recipe Filters */}
          {activeTab === 'recipes' && (
            <>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFilter} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Filter:</span>
                <select
                  value={recipeFilters.difficulty || ''}
                  onChange={(e) => setRecipeFilters({ ...recipeFilters, difficulty: e.target.value || null })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="">All Difficulties</option>
                  {recipeDifficulties.map(diff => (
                    <option key={diff} value={diff}>{diff}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSort} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Sort:</span>
                <select
                  value={recipeFilters.sortBy}
                  onChange={(e) => setRecipeFilters({ ...recipeFilters, sortBy: e.target.value })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="popularity">Popularity (Most Claps)</option>
                  <option value="title">Title (A-Z)</option>
                  <option value="time">Time (Shortest First)</option>
                  <option value="difficulty">Difficulty</option>
                </select>
              </div>
            </>
          )}

          {/* Activity Filters */}
          {activeTab === 'activities' && (
            <>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFilter} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Filter:</span>
                <select
                  value={activityFilters.category || ''}
                  onChange={(e) => setActivityFilters({ ...activityFilters, category: e.target.value || null })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="">All Categories</option>
                  {activityCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-text-secondary">Age:</span>
                  <input
                    type="number"
                    min="0"
                    max="18"
                    value={activityFilters.age}
                    onChange={(e) => setActivityFilters({ ...activityFilters, age: e.target.value })}
                    placeholder="Any"
                    className="w-20 px-2 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSort} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Sort:</span>
                <select
                  value={activityFilters.sortBy}
                  onChange={(e) => setActivityFilters({ ...activityFilters, sortBy: e.target.value })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="popularity">Popularity (Most Claps)</option>
                  <option value="title">Title (A-Z)</option>
                  <option value="time">Time (Shortest First)</option>
                  <option value="category">Category</option>
                </select>
              </div>
            </>
          )}

          {/* Story Filters */}
          {activeTab === 'stories' && (
            <>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFilter} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Filter:</span>
                <select
                  value={storyFilters.category || ''}
                  onChange={(e) => setStoryFilters({ ...storyFilters, category: e.target.value || null })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="">All Categories</option>
                  {storyCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-text-secondary">Age:</span>
                  <input
                    type="number"
                    min="0"
                    max="18"
                    value={storyFilters.age}
                    onChange={(e) => setStoryFilters({ ...storyFilters, age: e.target.value })}
                    placeholder="Any"
                    className="w-20 px-2 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSort} className="text-text-secondary text-sm" />
                <span className="text-sm font-semibold text-text-primary">Sort:</span>
                <select
                  value={storyFilters.sortBy}
                  onChange={(e) => setStoryFilters({ ...storyFilters, sortBy: e.target.value })}
                  className="px-3 py-1.5 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary"
                >
                  <option value="popularity">Popularity (Most Claps)</option>
                  <option value="title">Title (A-Z)</option>
                  <option value="category">Category</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-text-secondary">
          {activeTab === 'recipes' && (
            <span>Showing {getFilteredAndSortedRecipes.length} {getFilteredAndSortedRecipes.length === 1 ? 'recipe' : 'recipes'}</span>
          )}
          {activeTab === 'activities' && (
            <span>Showing {getFilteredAndSortedActivities.length} {getFilteredAndSortedActivities.length === 1 ? 'activity' : 'activities'}</span>
          )}
          {activeTab === 'stories' && (
            <span>Showing {getFilteredAndSortedStories.length} {getFilteredAndSortedStories.length === 1 ? 'story' : 'stories'}</span>
          )}
        </div>

        {/* Content Grid - Mobile optimized spacing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {activeTab === 'recipes' &&
            getFilteredAndSortedRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          {activeTab === 'activities' &&
            getFilteredAndSortedActivities.map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))}
          {activeTab === 'stories' &&
            getFilteredAndSortedStories.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
        </div>
      </PageHeader>

      {/* Submission Modals */}
    </div>
  );
}

